// Spanish copy of the Tabla de Retención Documental module.

export const SUPPORT_LABELS = {
  PAPEL: "Papel",
  ELECTRONICO: "Electrónico",
};

export const STATUS_LABELS = {
  BORRADOR: "Borrador",
  VIGENTE: "Vigente",
  OBSOLETA: "Obsoleta",
};

export const STATUS_COLORS = {
  BORRADOR: "warning",
  VIGENTE: "success",
  OBSOLETA: "default",
};

const CODE_PROBLEMS = {
  EMPTY_CODE: "El código es obligatorio",
  CODE_HAS_OUTER_SPACES: "El código no puede empezar ni terminar con espacios",
  CODE_TOO_LONG: "El código es demasiado largo",
  CODE_HAS_SLASH: "El código no puede contener «/»",
  CODE_RESERVED: "Ese código está reservado",
};

// Error codes answered by handleTrd -> what the archivist reads.
export const TRD_ERRORS = {
  FORBIDDEN: "No tienes permiso para esta acción. Si acabas de recibir el rol, vuelve a iniciar sesión o pide que guarden tu usuario de nuevo.",
  FAILED: "No se pudo completar la acción. Intenta de nuevo.",
  NOT_FOUND: "No se encontró la dependencia o la versión.",
  DUPLICATE_CODE: "Ya existe ese código.",
  EXISTS_IN_TRD: "Esa dependencia ya está en la TRD. Usa «Importar TRD existente» en lugar de crearla.",
  NOT_A_DRAFT: "Solo se edita una versión en borrador. Crea una versión nueva a partir de la vigente.",
  DRAFT_EXISTS: "Esa dependencia ya tiene un borrador abierto.",
  NO_PUBLISHED_VERSION: "La dependencia no tiene una versión vigente de la cual partir.",
  INVALID_APPROVAL: "Falta información del acto administrativo.",
  NOT_PUBLISHABLE: "La versión no se puede publicar todavía.",
  EMPTY_NAME: "El nombre es obligatorio",
  SERIES_MUST_BE_ARRAY: "La estructura de series no es válida",
  INVALID_SUPPORT: "El soporte no es válido",
  INVALID_RETENTION: "La retención debe ser un número entero de años (0 o más)",
  INVALID_SELECTION: "La selección debe ser un número entero de años (0 o más)",
  CT_WITH_SELECTION: "La conservación total no se combina con una selección",
  INVALID_TABLE_NUMBER: "El número de tabla debe ser un número entero",
  INVALID_FILETYPES: "Los tipos documentales no son válidos",
  DUPLICATE_FILETYPE: "Hay un tipo documental repetido",
  MISSING_ACT_NUMBER: "Falta el número del acto administrativo",
  MISSING_ISSUING_BODY: "Falta el órgano que expide el acto",
  INVALID_ACT_DATE: "La fecha del acto no es válida",
  NO_SERIES: "La dependencia no tiene series",
  SERIES_WITHOUT_SUBSERIES: "Toda serie necesita al menos una subserie",
  MISSING_RETENTION: "Falta el tiempo de retención",
  MISSING_DISPOSITION: "Falta la disposición final (conservación total, selección o el procedimiento)",
  MISSING_FILETYPES: "Falta al menos un tipo documental",
  ...CODE_PROBLEMS,
};

export const trdError = (code) => TRD_ERRORS[code] || TRD_ERRORS.FAILED;

// One line per validation error returned by SAVE_DRAFT / PUBLISH, naming where.
// The backend points at a position ("series[1].subseries[0].code"); with the
// draft on screen that becomes the serie and subserie the archivist sees.
export const describeErrors = (errors = [], draft = null) =>
  errors.map((e) => {
    const m = /^series\[(\d+)\](?:\.subseries\[(\d+)\])?/.exec(e.path || "");
    const serie = m && draft?.series?.[Number(m[1])];
    const subserie = serie && m[2] !== undefined ? serie.subseries?.[Number(m[2])] : null;
    const label = (kind, position, item, code) => {
      const name = item && (item.code || item.name) ? `${item.code || "sin código"} — ${item.name || "sin nombre"}` : null;
      return name ? `${kind} ${name}` : `${kind} n.º ${position + 1}`;
    };
    const where = [
      m ? label("Serie", Number(m[1]), serie) : (e.serie !== undefined ? `Serie ${e.serie}` : null),
      m && m[2] !== undefined ? label("Subserie", Number(m[2]), subserie) : (e.subserie !== undefined ? `Subserie ${e.subserie}` : null),
    ].filter(Boolean).join(" · ");
    const value = e.value ? ` («${e.value}»)` : "";
    return `${where ? `${where}: ` : ""}${trdError(e.code)}${value}`;
  });

// Two spellings are the same document type when they differ only in case,
// accents or spacing (the backend applies the same rule).
export const typeKey = (name) =>
  String(name == null ? "" : name)
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es").replace(/\s+/g, " ").trim();

export const emptySubserie = () => ({
  code: "",
  name: "",
  soporte: [],
  retention: { management: null, central: null },
  final_disposition: { total_conservation: false, selection: null },
  procedure: "",
  observaciones: "",
  filetypes: [],
});

export const emptySerie = () => ({ code: "", name: "", format_code: "", subseries: [emptySubserie()] });
